import { FC, useMemo, useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import Callout from "components/callout/callout"
import Button from "components/inputs/button"
import ButtonGroup from "components/buttons/buttonGroup"
import Section from "components/layout/section"
import ProcessingButton from "components/inputs/processingButton"
import FormErrors from "components/formParts/formErrors"
import Hint from "components/text/Hint"
import IconButton from "components/buttons/iconButton"
import { ReactComponent as UploadIcon } from "assets/icons/upload.svg"
import { ReactComponent as PhotoCameraIcon } from "assets/icons/photo_camera.svg"
import { ReactComponent as CloseIcon } from "assets/icons/close.svg"
import { ReactComponent as AutoAwesomeIcon } from "assets/icons/auto_awesome.svg"
import useStorages from "hooks/useStorages"
import useStorageLimits from "hooks/useStorageLimits"
import useLayout from "hooks/useLayout"
import useWindowDimensions from "hooks/useWindowDimensions"
import { DeviceSize, ProductAiSuggestion } from "types"
import { decodeFirebaseError } from "utils/firebaseHelpers"
import { suggestProductFromImages } from "services/firebaseApi"
import { compressImageFiles } from "utils/compressImageFile"

const MAX_AI_IMAGES = 3

interface SuggestProductFromImagesFormProps {
    storageId: string
    onSuccess?: (payload: { files: File[]; suggestion: ProductAiSuggestion }) => void
}

const getUsedToday = (date?: string, count?: number) => {
    const today = new Date().toISOString().slice(0, 10)
    return date === today ? (count || 0) : 0
}

const SuggestProductFromImagesForm: FC<SuggestProductFromImagesFormProps> = ({ storageId, onSuccess }) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const { addToast, hideDialog } = useLayout()
    const { deviceSize } = useWindowDimensions()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const cameraInputRef = useRef<HTMLInputElement>(null)

    const [files, setFiles] = useState<File[]>([])
    const [errors, setErrors] = useState<string[]>([])
    const [isAnalyzing, setIsAnalyzing] = useState(false)

    const { storage } = useStorages({ storageId })
    const currentProductCount = storage?.counters?.productCounter || 0
    const storagePlan = storage?.settings?.storagePlan
    const { canAddProduct, canUseAiProductSuggestions, limits } = useStorageLimits({
        storagePlan,
        currentProductCount,
    })

    const usedToday = getUsedToday(
        storage?.usage?.aiProductSuggestions?.date,
        storage?.usage?.aiProductSuggestions?.count,
    )
    const remainingToday = Math.max(0, (limits.aiProductSuggestionsPerDay || 0) - usedToday)
    const canAnalyze = canUseAiProductSuggestions && canAddProduct && remainingToday > 0

    const previews = useMemo(
        () => files.map((file) => ({ file, url: URL.createObjectURL(file) })),
        [files],
    )

    const addFiles = (incoming: File[]) => {
        const next = [...files, ...incoming].slice(0, MAX_AI_IMAGES)
        setFiles(next)
        setErrors([])
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) return
        addFiles(Array.from(event.target.files))
        event.target.value = ""
    }

    const handleAnalyze = async () => {
        if (!canAnalyze) return
        if (files.length === 0) {
            setErrors([t("errorSelectAiImages")])
            return
        }

        setIsAnalyzing(true)
        setErrors([])
        try {
            const compressed = await compressImageFiles(files)
            const result = await suggestProductFromImages(storageId, compressed)
            await queryClient.invalidateQueries({ queryKey: ['storage', storageId] })
            onSuccess?.({ files: compressed, suggestion: result.suggestion })
            addToast({ text: t("toastAiProductSuggestionsApplied"), colorScheme: "green" })
            hideDialog()
        } catch (error: unknown) {
            const errorMessage = decodeFirebaseError(error instanceof Error ? error.message : String(error))
            setErrors([errorMessage])
            addToast({ text: t(errorMessage), colorScheme: "red" })
        } finally {
            setIsAnalyzing(false)
        }
    }

    return (
        <Section>
        <div className="flex flex-col gap-y-4">
            {!canUseAiProductSuggestions && (
                <Callout type="warning">{t("errorAiProductSuggestionsNotSupported")}</Callout>
            )}
            {canUseAiProductSuggestions && !canAddProduct && (
                <Callout type="warning">{t("errorProductLimitReached", { limit: limits.products })}</Callout>
            )}
            {canUseAiProductSuggestions && canAddProduct && remainingToday === 0 && (
                <Callout type="warning">{t("errorAiProductSuggestionsDailyLimitReached", { limit: limits.aiProductSuggestionsPerDay })}</Callout>
            )}
            {canAnalyze && (
                <Callout type="info">
                    {t("aiProductSuggestionsRemaining", { remaining: remainingToday, limit: limits.aiProductSuggestionsPerDay })}
                </Callout>
            )}
            <Hint>{t("aiProductSuggestionsHint")}</Hint>
            {previews.length > 0 && (
                <div className="grid grid-cols-3 gap-3 w-fit">
                    {previews.map(({ file, url }) => (
                        <div key={`${file.name}-${file.lastModified}`} className="relative border rounded-md overflow-hidden w-28 h-28 border-gray-400">
                            <img src={url} alt={file.name} className="w-full h-full object-cover" />
                            {!isAnalyzing && (
                                <IconButton
                                    icon={<CloseIcon />}
                                    size="small"
                                    colorScheme="red"
                                    className="absolute top-1 right-1"
                                    onClick={() => setFiles(files.filter((item) => item !== file))}
                                />
                            )}
                        </div>
                    ))}
                </div>
            )}
            <div className="flex flex-col gap-2 md:flex-row md:flex-wrap">
                <Button wideOnMobile type="button" design="white" disabled={isAnalyzing || files.length >= MAX_AI_IMAGES} onClick={() => fileInputRef.current?.click()}>
                    <UploadIcon />
                    <span>{t("buttonUploadImages")}</span>
                </Button>
                {deviceSize !== DeviceSize.LG && (
                    <Button wideOnMobile type="button" design="white" disabled={isAnalyzing || files.length >= MAX_AI_IMAGES} onClick={() => cameraInputRef.current?.click()}>
                        <PhotoCameraIcon />
                        <span>{t("buttonTakePhoto")}</span>
                    </Button>
                )}
            </div>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/*"
                multiple
                className="hidden"
                onChange={handleFileChange}
                disabled={isAnalyzing}
            />
            <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleFileChange}
                disabled={isAnalyzing}
            />
            <ButtonGroup>
                {isAnalyzing ? (
                    <ProcessingButton design="primary" />
                ) : (
                    <Button wideOnMobile type="button" design="primary" disabled={!canAnalyze || files.length === 0} onClick={handleAnalyze}>
                        <AutoAwesomeIcon />
                        <span>{t("buttonAnalyzeProductImages")}</span>
                    </Button>
                )}
            </ButtonGroup>
            {errors.length > 0 && <FormErrors errors={errors} />}
        </div>
        </Section>
    )
}

export default SuggestProductFromImagesForm
